import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import moment from 'moment';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { MenuItem } from 'primeng/api';
import { Message } from 'primeng/message';
import { PopoverModule } from 'primeng/popover';
import { StepsModule } from 'primeng/steps';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { OrganizationSearchFormComponent } from 'src/app/@shared/components/organization-search-form/organization-search-form.component';

// Modèles
import { AccountingMethod } from 'src/app/@shared/models/accounting/accounting-method.model';
import { AccountingPeriod } from 'src/app/@shared/models/accounting/accounting-period.model';
import { Client } from 'src/app/@shared/models/client/client.model';
import { Country } from 'src/app/@shared/models/core/country.model';
import { LegalStatus } from 'src/app/@shared/models/taxation/legal-status.model';
import { Organization } from 'src/app/@shared/models/legal-entity/organization.model';
import { OrganizationActivity } from 'src/app/@shared/models/legal-entity/organization-activity.model';
import { OrganizationSize } from 'src/app/@shared/models/legal-entity/organization-size.model';
import { Tax } from 'src/app/@shared/models/core/tax.model';
import { TaxationType } from 'src/app/@shared/models/taxation/taxation-type.model';
import { Timezone } from 'src/app/@shared/models/core/timezone.model';

// Utils
import { Filter } from 'src/app/@shared/utilities/models/filter';
import { Filtering } from 'src/app/@shared/utilities/array/filtering';
import { Sorter } from 'src/app/@shared/utilities/models/sorter';

// Animations
import { expandHeight, stepAnimation } from 'src/app/@shared/animations/animations';

@Component({
    selector: 'app-create-client-form',
    imports: [
        ReactiveFormsModule,
        RouterModule,
        ButtonModule,
        CheckboxModule,
        DatePickerModule,
        SelectModule,
        InputTextModule,
        Message,
        PopoverModule,
        StepsModule,
        TextareaModule,
        ToggleSwitchModule,
        OrganizationSearchFormComponent,
    ],
    templateUrl: './create-client-form.component.html',
    styleUrls: ['./create-client-form.component.scss'],
    animations: [expandHeight, stepAnimation]
})
export class CreateClientFormComponent extends FormBaseComponent {

    /*----------------------------------------------------------------------------------------
     * Vue
     ----------------------------------------------------------------------------------------*/

    steps: MenuItem[] = [
        { label: 'Pays' },
        { label: 'Société' },
        { label: 'Fiscalité' },
        { label: 'TGC / TVA' },
        { label: 'Comptabilité' },
        // { label: 'Validation' },
    ];

    // Définition de l'étape active
    activeStep: number = 0;

    @Output() created: EventEmitter<Client> = new EventEmitter<Client>();

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _accountingMethods: AccountingMethod[] = [];
    public get accountingMethods(): AccountingMethod[] {
        return this._accountingMethods;
    }
    public set accountingMethods(accountingMethods: AccountingMethod[]) {
        let emptyArray: AccountingMethod[] = [];
        this._accountingMethods = emptyArray.concat(accountingMethods);
    }

    private _client: Client = Client.init();
    public get client(): Client {
        return this._client;
    }
    @Input()
    public set client(client: Client) {
        this._client = client;
    }

    private _countries: Country[] = [];
    public get countries(): Country[] {
        return this._countries;
    }
    public set countries(countries: Country[]) {
        let emptyArray: Country[] = [];
        this._countries = emptyArray.concat(countries);
    }

    private _legalStatuses: LegalStatus[] = [];
    public get legalStatuses(): LegalStatus[] {
        return this._legalStatuses;
    }
    public set legalStatuses(legalStatuses: LegalStatus[]) {
        let emptyArray: LegalStatus[] = [];
        this._legalStatuses = emptyArray.concat(legalStatuses);
    }

    private _mainActivities: OrganizationActivity[] = [];
    public get mainActivities(): OrganizationActivity[] {
        return this._mainActivities;
    }
    public set mainActivities(mainActivities: OrganizationActivity[]) {
        let emptyArray: OrganizationActivity[] = [];
        this._mainActivities = emptyArray.concat(mainActivities);
    }

    private _organizations: Organization[] = [];
    public get organizations(): Organization[] {
        return this._organizations;
    }
    public set organizations(organizations: Organization[]) {
        let emptyArray: Organization[] = [];
        this._organizations = emptyArray.concat(organizations);
    }

    private _sizes: OrganizationSize[] = [];
    public get sizes(): OrganizationSize[] {
        return this._sizes;
    }
    public set sizes(sizes: OrganizationSize[]) {
        let emptyArray: OrganizationSize[] = [];
        this._sizes = emptyArray.concat(sizes);
    }

    private _taxCodes: Tax[] = [];
    public get taxCodes(): Tax[] {
        return this._taxCodes;
    }
    public set taxCodes(taxCodes: Tax[]) {
        let emptyArray: Tax[] = []
        this._taxCodes = emptyArray.concat(taxCodes);
    }

    private _taxationTypes: TaxationType[] = []
    public get taxationTypes(): TaxationType[] {
        return this._taxationTypes;
    }
    public set taxationTypes(taxationTypes: TaxationType[]) {
        let emptyArray: TaxationType[] = [];
        this._taxationTypes = emptyArray.concat(taxationTypes);
    }

    private _timezones: Timezone[] = [];
    public get timezones(): Timezone[] {
        return this._timezones;
    }
    public set timezones(timezones: Timezone[]) {
        let emptyArray: Timezone[] = [];
        this._timezones = emptyArray.concat(timezones);
        if (this._timezones.length > 0) {
            this.form.patchValue({
                timezone: this._timezones[0],
            });
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onClickCountry(country: Country): void {
        this._selectCountry(country);
        this.next();
    }

    public onClickVatSubject(vatSubject: boolean): void {
        this._handleVatSubjectChange(vatSubject);
        if (!vatSubject) {
            this.next();
        }
    }

    public onOrganizationSelected(organization: Organization) {
        this._selectOrganization(organization);
    }

    public previous(): void {
        this.activeStep--;
        this._manageForm();
    }

    public next(): void {
        this.activeStep++;
        this._manageForm();
    }

    public get vatName(): string {
        return this.form.get('country')?.value?.alpha2 === 'NC' ? 'TGC' : 'TVA';
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override buildForm(): void {
        this.form = this.formBuilder.group({
            // Données générales
            country: [null, Validators.required],
            commercialName: '',
            businessContext: '',
            mainActivity: [null, Validators.required],
            name: ['', Validators.required],
            identifier: '',
            organization: null,
            search: '',
            size: null,
            timezone: [null, Validators.required],
            // Fiscalité
            legalStatus: [null, Validators.required],
            taxationType: [null, Validators.required],
            vatSubject: false,
            vatDateFrom: null,
            vatDefault: null,
            // Comptabilité
            accountingMethod: [null, Validators.required],
            periodDateFrom: this._getInitialDateFrom(),
            periodDateTo: this._getInitialDateTo(),
            // Mdoules
            bankingActive: true,
            ocrActive: true,
            recoveryActive: true,
        });
        this._manageForm();
    }

    protected override loadData(): void {
        this._loadCountries();
        this._loadMainActivities();
        this._loadSizes();
    }

    protected override save(): void {
        this._mapFromForm();
        this.appService.uiBlocked = true;
        this.clientService.save$(this.client, { select: false }).pipe(
            finalize(() => this.appService.uiBlocked = false)
        ).subscribe(client => {
            this.created.emit(client);
        });
    }

    protected override subscribeToObservables(): void {

        // Modification du statut juridique
        this.subscriptions.add(
            this.form.get('legalStatus')?.valueChanges.subscribe(value => {
                if (value) this._loadTaxationTypes(value);
                this._mapFromForm();
                this._manageForm();
            }),
        );

        // Modification du type d'imposition
        this.subscriptions.add(
            this.form.get('taxationType')?.valueChanges.subscribe(value => {
                if (value) this._loadAccountingMethods(value);
            }),
        );

        // Modification de la saisie de la TVA
        this.subscriptions.add(
            this.form.get('vatSubject')?.valueChanges.subscribe(value => {
                // On vérifie si value est null
                if (value === null) return;

                this._mapFromForm();
                this._manageForm();
                if (!value) {
                    this.form.patchValue({
                        vatDateFrom: null,
                    });
                } else {
                    // Par défaut on prendra la date de début de la période comptable
                    this.form.patchValue({
                        vatDateFrom: this.form.get('periodDateFrom')?.value,
                    });
                }
            }),
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Renvoie le premier jour de l'année en cours
     */
    private _getInitialDateFrom(): Date {
        return new Date(new Date().getFullYear(), 0, 1);
    }

    /**
     * Renvoie le dernier jour de l'année en cours
     */
    private _getInitialDateTo(): Date {
        return new Date(new Date().getFullYear(), 11, 31);
    }

    private _handleVatSubjectChange(vatSubject: boolean): void {
        this.form.get('vatSubject')?.setValue(vatSubject);
    }

    private _loadAccountingMethods(taxationType: TaxationType): void {
        let filter: Filter = new Filter({
            filters: [],
            and: false,
        });
        if (taxationType.accrualAccounting) {
            filter.filters.push(new Filter({
                property: 'code',
                value: 'commitment',
            }));
        }
        if (taxationType.cashAccounting) {
            filter.filters.push(new Filter({
                property: 'code',
                value: 'cash',
            }));
        }
        let sorter: Sorter = new Sorter({
            property: 'label',
        });

        this.accountingMethodService.getList$({
            sorters: [sorter],
        }).subscribe(accountingMethods => {
            this.accountingMethods = Filtering.filter(accountingMethods, [filter]);
            // Sélection de la méthode par défaut
            this.form.patchValue({
                accountingMethod: this.accountingMethods.find(am => am.code === taxationType.defaultAccountingMethod.code),
            });
        });
    }

    private _loadCountries(): void {
        let filter: Filter = new Filter({
            property: 'enableForClients',
            value: true,
        });
        let sorter: Sorter = new Sorter({
            property: 'name',
        });
        this.countryService.getList$({
            filters: [filter],
            sorters: [sorter],
        }).subscribe(countries => {
            this.countries = countries;
        });
    }

    private _loadLegalStatuses(country: Country): void {
        let filter: Filter = new Filter({
            property: 'country',
            value: country.alpha2,
        });
        let sorter: Sorter = new Sorter({
            property: 'label',
        });

        this.legalStatusService.getList$({
            filters: [filter],
            sorters: [sorter],
        }).subscribe(legalStatuses => {
            this.legalStatuses = legalStatuses;
        });
    }

    private _loadMainActivities(): void {
        this.organizationActivityService.getList$().subscribe((mainActivities: OrganizationActivity[]) => {
            this.mainActivities = mainActivities;
        });
    }

    private _loadSizes(): void {
        let sorter: Sorter = new Sorter({
            property: 'code',
        });
        this.organizationSizeService.getList$({
            sorters: [sorter],
        }).subscribe((sizes: OrganizationSize[]) => {
            this.sizes = sizes;
        });
    }

    private _loadTaxes(): void {
        let filter: Filter = new Filter({
            property: 'country/alpha2',
            value: this.form.get('country')?.value?.alpha2,
        });
        let sorter = new Sorter({
            property: 'rate'
        });
        this.taxService.getList$({
            filters: [filter],
            sorters: [sorter]
        }).subscribe(taxes => this.taxCodes = taxes);
    }

    private _loadTaxationTypes(legalStatus: LegalStatus): void {
        let filter: Filter = new Filter({
            property: 'legalStatus/legalStatus',
            value: legalStatus.code,
        });
        let sorter: Sorter = new Sorter({
            property: 'legalStatus/default',
            descending: true,
        });

        this.taxationTypeService.getList$({
            filters: [filter],
            sorters: [sorter],
        }).subscribe(taxationTypes => {
            this.taxationTypes = taxationTypes;
            if (this.taxationTypes.length > 0) {
                this.form.patchValue({
                    taxationType: this.taxationTypes[0],
                });
            }
        });
    }

    private _loadTimezones(country: Country): void {
        let filter: Filter = new Filter({
            property: 'countries',
            value: country.alpha2,
        });
        let sorter: Sorter = new Sorter({
            property: 'label',
        });

        this.timezoneService.getList$({
            filters: [filter],
            sorters: [sorter],
        }).subscribe((timezones: Timezone[]) => {
            this.timezones = timezones;
        });
    }

    private _manageForm(): void {

        // Sélection du pays
        if (!!this.client?.country) {
            // Activation des champs
            this.form.get('search')?.enable();
            this.form.get('name')?.enable();
            this.form.get('commercialName')?.enable();
            this.form.get('identifier')?.enable();
            this.form.get('timezone')?.enable();

            // Sauf certains pour la Calédonie et la Polynésie (données issues du référentiel)
            if (this.client.isCaledonian || this.client.isPolynesian) {
                this.form.get('name')?.disable();
                this.form.get('identifier')?.disable();
            }
        } else {
            this.form.get('search')?.disable();
            this.form.get('name')?.disable();
            this.form.get('commercialName')?.disable();
            this.form.get('identifier')?.disable();
            this.form.get('timezone')?.disable();
        }

        // Selon forme juridique
        if (!!this.client?.taxationSettings.legalStatus) {
            this.form.get('taxationType')?.enable({ emitEvent: false });
            this.form.get('accountingMethod')?.enable({ emitEvent: false });
            this.form.get('vatSubject')?.enable({ emitEvent: false });
        } else {
            this.form.get('taxationType')?.disable({ emitEvent: false });
            this.form.get('accountingMethod')?.disable({ emitEvent: false });
            this.form.get('vatSubject')?.disable({ emitEvent: false });
        }

        // Selon assujettissement à la TVA
        if (this.client?.taxationSettings.vatSubject) {
            this.form.get('vatDateFrom')?.setValidators(Validators.required);
        } else {
            this.form.get('vatDateFrom')?.clearValidators();
        }
        this.form.get('vatDateFrom')?.updateValueAndValidity();
        this.form.get('vatDefault')?.updateValueAndValidity();
    }

    private _mapFromForm(): void {
        this.client.country = this.form.get('country')?.value;
        this.client.commercialName = this.form.get('commercialName')?.value;
        this.client.businessContext = this.form.get('businessContext')?.value;
        this.client.mainActivity = this.form.get('mainActivity')?.value;
        this.client.name = this.form.get('name')?.value;
        this.client.organization = this.form.get('organization')?.value;
        this.client.identifier = this.form.get('identifier')?.value;
        this.client.size = this.form.get('size')?.value;
        this.client.timezone = this.form.get('timezone')?.value;
        this.client.taxationSettings.legalStatus = this.form.get('legalStatus')?.value;
        this.client.taxationSettings.taxationType = this.form.get('taxationType')?.value;
        this.client.accountingSettings.accountingMethod = this.form.get('accountingMethod')?.value;
        this.client.taxationSettings.vatSubject = this.form.get('vatSubject')?.value;
        this.client.taxationSettings.vatDateFrom = moment(this.form.get('vatDateFrom')?.value);
        this.client.taxationSettings.vatDefault = this.form.get('vatDefault')?.value;

        if (!this.client.accountingPeriods.length) {
            let accountingPeriod = AccountingPeriod.init();
            accountingPeriod.dateFrom = moment(this.form.get('periodDateFrom')?.value);
            accountingPeriod.dateTo = moment(this.form.get('periodDateTo')?.value);
            this.client.accountingPeriods = [accountingPeriod];
        } else {
            this.client.accountingPeriods[0].dateFrom = moment(this.form.get('periodDateFrom')?.value);
            this.client.accountingPeriods[0].dateTo = moment(this.form.get('periodDateTo')?.value);
        }
    }

    private _mapToForm(): void {
        this.form.patchValue({
            commercialName: this.client.commercialName,
            name: this.client.name,
            identifier: this.client.identifier,
            organization: this.client.organization,
        });
        this._manageForm();
    }

    private _selectCountry(country: Country): void {
        // Mapping
        this.client.country = country;
        this.form.get('country')?.setValue(country);
        // Actions annexes
        this._manageForm();
        this._loadTimezones(country);
        this._loadLegalStatuses(country);
        this._loadTaxes();
    }

    private _selectOrganization(organization: Organization): void {
        this.client.name = organization.name;
        this.client.commercialName = organization.acronym;
        this.client.identifier = organization.identifier;
        this.client.organization = organization;
        this._mapToForm();
    }

}

